import {
  Menu,
  ChevronDown,
  ChevronUp,
  Bell,
  Search,
  ChevronLeft,
  ChevronRight,
  Check,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { createContext, useRef } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import AccountsMenu from "./AccountsMenu";
import { createPortal } from "react-dom";
import ZinduaLogo from "../../src/assets/images/ZinduaLogo.svg";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import axios from "axios";
import { api_endpoint } from "../constants/constant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import { useSelector } from "react-redux";
import { Button } from "../../@/components/ui/button";

export const SidebarContext = createContext();

const SIDEBAR_EXPANDED = 230;
const SIDEBAR_COLLAPSED = 65;

export default function DesktopSidebar({
  children,
  expanded,
  setExpanded,
  moreMenu = [],
  adminMode = false,
  onToggleAdminMode,
  showAdminToggle = false,
  modeLabel = "Admin",
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [isPortalDropdownOpen, setIsPortalDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const { getToken } = useAuth();
  const { user } = useUser();
  const roles = useSelector((s) => s.auth.roles) || [];
  const hasAdminAccess = roles.includes("Admin") || roles.includes("Manager");

  const currentHostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const currentHost = typeof window !== "undefined" ? window.location.host : "";

  const isLocalhost =
    currentHostname === "localhost" || currentHostname === "127.0.0.1";

  const adminPortalOrigin = (() => {
    if (typeof window === "undefined") return "https://dash.zinduaschool.com";
    if (isLocalhost) return window.location.origin;
    if (currentHostname.includes("dash-test.zinduaschool.com"))
      return "https://dash-test.zinduaschool.com";
    if (currentHostname.includes("dash.zinduaschool.com"))
      return "https://dash.zinduaschool.com";
    if (currentHostname.includes("lms-tm-app.pages.dev"))
      return "https://lms-tm-app.pages.dev";
    return window.location.origin;
  })();

  const adminPortalLabel = isLocalhost ? currentHost : currentHostname;
  const isAdminPortal =
    isLocalhost ||
    currentHostname.includes("dash.zinduaschool.com") ||
    currentHostname.includes("dash-test.zinduaschool.com") ||
    currentHostname.includes("lms-tm-app.pages.dev");
  const isLearningPortal = currentHostname.includes("app.zinduaschool.com");
  const isStudentPortal = currentHostname.includes("alpha.zinduaschool.com");

  // Match the other sidebar UX: dim background when portal selector is open
  // (DropdownMenu doesn't include a backdrop by default)
  const portalBackdrop = isPortalDropdownOpen ? (
    <div className="fixed inset-0 bg-neutral-950/10 backdrop-blur-sm z-40" />
  ) : null;

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const token = await getToken();
        if (!token) return;
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await axios.get(
          `${api_endpoint}/notification/user-notifications`,
          config
        );
        const notifications = response.data.notifications || [];
        const unread = notifications.filter((n) => !n.is_read).length;
        setUnreadCount(unread);
      } catch (error) {
        setUnreadCount(0);
      }
    };
    fetchUnreadCount();
  }, [user, getToken]);

  // Bottom section icons
  const bottomIcons = [
    // {
    //   key: "search",
    //   tooltip: "Search",
    //   content: expanded && (
    //     <div className="flex items-center w-full text-[var(--color-sidebar-foreground)]">
    //       <span className="shrink-0 w-8 h-8 text-(--neutral-600)">
    //         <Search className="w-7 h-7 text-(--neutral-600)" />
    //       </span>
    //       <input
    //         type="text"
    //         placeholder="Search"
    //         className="
    //       flex-1 border-none outline-none bg-transparent text-sm
    //       text-(--neutral-600)
    //       placeholder:text-(--neutral-700)
    //       font-body ml-3
    //     "
    //       />
    //     </div>
    //   ),
    //   icon: <Search className="w-6 h-6 text-(--neutral-700)" />,
    // },
    {
      key: "notifications",
      as: "button",
      icon: (
        <span className="relative flex items-center justify-center">
          <Bell className="w-6 h-6 text-(--neutral-700)" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-(--color-primary)" />
          )}
        </span>
      ),
      tooltip: "Notifications",
      onClick: () => navigate({ to: "/notifications" }),
      content: expanded && (
        <span className="flex items-center">
          <span className="relative flex items-center justify-center">
            <Bell className="w-6 h-6 text-(--neutral-600)" />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-(--color-primary)" />
            )}
          </span>
          <p className="ml-3 text-(--neutral-900) font-body">
            Notifications
          </p>
        </span>
      ),
    },
    {
      key: "account",
      // Render as a div wrapper to avoid nesting a <button> inside a <button>
      // (AccountsMenu contains its own button trigger)
      as: "div",
      icon: (
        <AccountsMenu placement="sidebar" collapsed={!expanded} />
      ),
      tooltip: "Account",
      content: expanded && <AccountsMenu placement="sidebar" collapsed={false} />,
      className: "mt-2",
    },
  ];

  return (
    <>
      {portalBackdrop}
      <aside
        className={`
         hidden md:flex fixed left-0 top-0 z-50 h-screen flex-col transition-all duration-300
      bg-(--neutral-50) text-(--neutral-900)
      border-r border-(--neutral-200)
        ${expanded ? "w-57.5" : "w-16.25"}
      `}
        style={{
          width: expanded ? `${SIDEBAR_EXPANDED}px` : `${SIDEBAR_COLLAPSED}px`,
          minWidth: expanded ? `${SIDEBAR_EXPANDED}px` : `${SIDEBAR_COLLAPSED}px`,
          maxWidth: expanded ? `${SIDEBAR_EXPANDED}px` : `${SIDEBAR_COLLAPSED}px`,
        }}
      >
      {/* Sidebar header */}
      <div className="flex px-3 py-1 pt-4 items-center min-w-0 gap-2">
        <div className="flex flex-col items-center justify-center gap-0">
          <img
            src={ZinduaLogo}
            alt="Zindua"
            className="w-8 h-8 object-contain"
          />
        </div>
        <div
          className={cn(
            "flex flex-col flex-1 min-w-0 transition-all",
            expanded ? "ml-1" : "ml-0"
          )}
        >
          <h6
            className={`
              overflow-hidden hidden md:block whitespace-nowrap leading-tight transition-all
              ${expanded ? "w-40" : "w-0"}
            `}
            style={{ margin: 0, lineHeight: "1.1" }}
          >
            Zindua LMS
          </h6>
          <p
            className={`
              overflow-hidden hidden md:block whitespace-nowrap transition-all text-(--neutral-600) leading-tight text-[13px] mt-1
              ${expanded ? "w-40" : "w-0"}
            `}
          >
            Admin/TM Portal
          </p>
        </div>
        {expanded && (
          <DropdownMenu open={isPortalDropdownOpen} onOpenChange={setIsPortalDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="p-1 rounded-md hover:bg-(--neutral-100) focus:outline-none"
                aria-label="Switch portal"
                aria-expanded={isPortalDropdownOpen}
                onPointerDown={(event) => {
                  // Prevent Radix pointer-down behavior that can open the menu
                  // under the cursor and immediately select the first item.
                  event.preventDefault();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  // Open/close AFTER the click finishes so the mouse-up can't
                  // accidentally land on the first menu item.
                  setTimeout(() => {
                    setIsPortalDropdownOpen((prev) => !prev);
                  }, 0);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setIsPortalDropdownOpen((prev) => !prev);
                  }
                }}
              >
                <div className="flex flex-col items-center justify-center">
                  <ChevronUp className="w-4 h-4" strokeWidth={2.5} />
                  <ChevronDown className="w-4 h-4 -mt-1.5" strokeWidth={2.5} />
                </div>
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="absolute -right-65 -top-12 w-64 shadow-lg z-50 border-(--neutral-300)"
              align="end"
              side="right"
            >
              <DropdownMenuItem
                onClick={() => window.open(adminPortalOrigin, "_self")}
                className="flex items-start gap-3 p-3"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0">
                  <img
                    src={ZinduaLogo}
                    alt="Zindua"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-(--neutral-950)">
                      Admin Portal
                    </span>
                    {isAdminPortal && (
                      <Check className="w-6 h-6 text-(--color-success)" />
                    )}
                  </div>
                  <span className="text-xs text-(--neutral-700)">
                    {adminPortalLabel}
                  </span>
                </div>
              </DropdownMenuItem>

              {/* <DropdownMenuItem
                onClick={() => window.open("https://app.zinduaschool.com", "_self")}
                className="flex items-start gap-3 p-3"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0">
                  <img
                    src={ZinduaLogo}
                    alt="Zindua"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-(--neutral-950)">
                      Learning Portal
                    </span>
                    {isLearningPortal && (
                      <Check className="w-6 h-6 text-(--color-success)" />
                    )}
                  </div>
                  <span className="text-xs text-(--neutral-700)">
                    app.zinduaschool.com
                  </span>
                </div>
              </DropdownMenuItem> */}

              <DropdownMenuItem
                onClick={() => window.open("https://alpha.zinduaschool.com", "_blank")}
                className="flex items-start gap-3 p-3"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0">
                  <img
                    src={ZinduaLogo}
                    alt="Zindua"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-(--neutral-950)">
                      Student Portal
                    </span>
                    {isStudentPortal && (
                      <Check className="w-5 h-5 text-(--color-success)" />
                    )}
                  </div>
                  <span className="text-xs text-(--neutral-700)">
                    alpha.zinduaschool.com
                  </span>
                </div>
              </DropdownMenuItem>

            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {expanded && showAdminToggle && typeof onToggleAdminMode === "function" && (
        <div className="px-3 py-1">
          <Button
            variant="outline"
            onClick={onToggleAdminMode}
            className={cn(
              "w-full rounded-md border px-3 py-1 font-heading transition-colors  flex items-center hover:bg-transparent hover:text-inherit hover:border-(--neutral-300)",
              "flex items-center",
              adminMode
                ? "border-(--color-success) text-(--color-success) hover:border-(--color-success) hover:text-(--color-success)"
                : "border-(--neutral-300) text-(--neutral-600)"
            )}
            aria-pressed={adminMode}
          >
            {/* left spacer to keep label visually centered */}
            {/* <span aria-hidden className="w-7 shrink-0" /> */}

            <span className="flex-1 text-center">
              {adminMode ? `${modeLabel} Mode On` : `${modeLabel} Mode Off`}
            </span>

            <span
              className={cn(
                "shrink-0 inline-flex items-center justify-center rounded-md",
                adminMode
                  ? "text-(--color-success)"
                  : "text-(--neutral-500)"
              )}
            >
              {adminMode ? (
                <ToggleRight className="w-6 h-6" />
              ) : (
                <ToggleLeft className="w-6 h-6" />
              )}
            </span>
          </Button>
        </div>
      )}

      {/* Main links */}
      <div className="flex-1 min-h-0 py-1 overflow-y-auto scrollbar-thin scrollbar-thumb-[var(--neutral-300)] scrollbar-track-transparent">
        <div className="flex h-full flex-col min-h-0">
          <div className="flex-none pr-1">
            <div
              className={cn(
                "flex flex-col gap-0.5",
                expanded && "items-center"
              )}
            >
              {children}
            </div>
          </div>

          {moreMenu.length > 0 && (
            <div className="flex-none pt-1 pr-1">
              <SidebarLink
                as="button"
                expanded={expanded}
                tooltip="More"
                onClick={() => setMoreOpen((v) => !v)}
                aria-expanded={moreOpen}
                aria-controls="sidebar-more-menu"
              >
                {expanded ? (
                  <>
                    {moreOpen ? (
                      <ChevronUp className="w-5 h-5 mr-2" />
                    ) : (
                      <ChevronDown className="w-5 h-5 mr-2" />
                    )}
                    <span>More</span>
                  </>
                ) : moreOpen ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </SidebarLink>

              {expanded ? (
                <div
                  className={cn(
                    "transition-[max-height] duration-300 ease-in-out overflow-hidden",
                    moreOpen ? "max-h-64" : "max-h-0"
                  )}
                >
                  <div
                    id="sidebar-more-menu"
                    className="ml-6 flex flex-col max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-[var(--neutral-300)] scrollbar-track-transparent"
                  >
                    {moreMenu.map((item) => (
                      <SidebarLink
                        key={item.path}
                        as={Link}
                        to={item.path}
                        expanded={expanded}
                        tooltip={item.name}
                        isSubLink
                        className="pl-4 pr-2 py-1"
                      >
                        {item.name}
                      </SidebarLink>
                    ))}
                  </div>
                </div>
              ) : (
                moreOpen && (
                  <div
                    id="sidebar-more-menu"
                    className="flex flex-col gap-1 px-1"
                  >
                    {moreMenu.map((item) => (
                      <SidebarIconLink
                        key={item.path}
                        as={Link}
                        to={item.path}
                        icon={<item.icon className="w-5 h-5" />}
                        tooltip={item.name}
                        active={location.pathname === item.path}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom section */}
      <div className="flex flex-col gap-0.5 pt-1 pb-3 pr-1 mt-auto w-full shrink-0 bg-(--neutral-50) border-(--neutral-200)">
        <div
          className={cn("flex flex-col gap-0.5", expanded && "items-center")}
        >
          {bottomIcons.map(({ key, icon, tooltip, onClick, content, as, className }) => {
            return (
              <SidebarLink
                key={key}
                as={as || "button"}
                expanded={expanded}
                tooltip={tooltip}
                onClick={onClick}
                className={cn(expanded ? "justify-start" : "justify-center", className)}
              >
                {expanded ? content : icon}
              </SidebarLink>
            );
          })}
        </div>
      </div>
      <SidebarEdgeToggle expanded={expanded} setExpanded={setExpanded} />
      </aside>
    </>
  );
}

// SidebarLink: full-width, proportional, hover, tooltip on collapsed
export function SidebarLink({
  as = "div",
  children,
  expanded,
  tooltip,
  className = "",
  style = {},
  active = false,
  isSubLink = false,
  ...props
}) {
  const Comp = as;
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  // Find the icon (first child) for collapsed mode
  const icon = Array.isArray(children) ? children[0] : children;

  // Determine if link is active (for Link components)
  // Support links with query strings (e.g. /live-classes?tab=upcoming)
  const currentFullPath = `${location.pathname}${location.search || ""}`;
  const toValue = props.to;
  const isActive =
    active ||
    (typeof toValue === "string" &&
      (toValue.includes("?")
        ? currentFullPath === toValue
        : location.pathname === toValue));

  // Tooltip positioning: right of icon, vertically centered
  return (
    <div
      className={`relative w-full`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      style={{ minHeight: 30 }}
    >
      <Comp
        {...props}
        className={cn(
          "flex items-center w-full px-1.5 py-0 group",
          !expanded && "justify-center",
          className
        )}
        style={style}
      >
        <span
          className={cn(
            "inline-flex items-center gap-0 pl-2.5 pr-2 py-1 rounded-[10px] transition-all duration-200 ease-out w-full mx-auto",
            !expanded && "justify-center w-auto mx-0",
            isActive
              ? "bg-(--neutral-100) text-(--color-primary)"
              : "text-(--neutral-900) hover:bg-(--neutral-100) group-hover:text-(--color-primary)",
            isSubLink ? "text-[14px]" : "text-[15px]"
          )}
        >
          {expanded ? children : icon}
        </span>
      </Comp>
      {/* Tooltip overlay when collapsed and hovered */}
      {!expanded && hovered && tooltip && (
        <div
          className="fixed z-9999 bg-(--color-primary) shadow-lg rounded-md px-3 py-2 text-sm font-body text-white"
          style={{
            left: ref.current
              ? ref.current.getBoundingClientRect().right + 8
              : 80,
            top: ref.current
              ? ref.current.getBoundingClientRect().top +
                ref.current.getBoundingClientRect().height / 2 -
                20
              : 0,
            transform: "translateY(-50%)",
            minWidth: 120,
            pointerEvents: "none",
          }}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
}

// For collapsed more menu: icon + tooltip
function SidebarIconLink({
  as = "div",
  icon,
  tooltip,
  active,
  indent,
  ...props
}) {
  const Comp = as;
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      style={{ minHeight: 20 }}
    >
      <Comp
        {...props}
        className={`
          flex items-center justify-center px-2 py-2 text-sm w-full rounded-md transition-all duration-200 ease-out
          ${
            active
              ? "bg-(--neutral-100) text-(--color-primary)"
              : "text-(--neutral-600) hover:bg-(--neutral-100) hover:text-(--neutral-900)"
          }
          ${indent ? "ml-6" : ""}
        `}
        style={{ minWidth: 20 }}
      >
        {icon}
      </Comp>
      {hovered && tooltip && (
        <div
          className="fixed z-9999 bg-(--color-primary) rounded-md px-3 py-2 text-sm font-body text-white"
          style={{
            left: ref.current
              ? ref.current.getBoundingClientRect().right + 8
              : 80,
            top: ref.current
              ? ref.current.getBoundingClientRect().top +
                ref.current.getBoundingClientRect().height / 2 -
                20
              : 0,
            transform: "translateY(-50%)",
            minWidth: 120,
            pointerEvents: "none",
          }}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
}

function SidebarEdgeToggle({ expanded, setExpanded }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setExpanded((prev) => !prev)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
      className="group absolute -right-3 top-0 hidden h-full w-6 items-center justify-center focus:outline-none md:flex z-10"
    >
      {/* Animated vertical line */}
      <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
        <span
          className={`
            h-full w-0.5 rounded-full
            transition-all duration-200 ease-out
            ${
              isHovered
                ? "bg-(--color-primary) shadow-[0_0_8px_rgba(var(--color-primary-rgb),0.5)]"
                : "bg-(--neutral-300)"
            }
          `}
          style={{
            transform: isHovered ? "scaleY(1)" : "scaleY(0.3)",
            transformOrigin: "center",
          }}
        />
      </span>

      {/* Chevron icon with background */}
      <span
        className={`
          relative flex items-center justify-center
          w-6 h-6 rounded-full
          transition-all duration-300 ease-out
          ${
            isHovered
              ? "bg-(--color-primary) shadow-lg scale-110"
              : "bg-(--neutral-200) scale-0"
          }
        `}
      >
        <span
          className={`
          transition-all duration-300
          ${isHovered ? "opacity-100 rotate-0" : "opacity-0 rotate-180"}
        `}
        >
          {expanded ? (
            <ChevronLeft className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          ) : (
            <ChevronRight
              className="w-3.5 h-3.5 text-white"
              strokeWidth={2.5}
            />
          )}
        </span>
      </span>

      {/* Pulsing effect on hover */}
      {isHovered && (
        <span
          className="absolute w-8 h-8 rounded-full bg-(--color-primary) opacity-20 animate-ping"
          style={{ animationDuration: "1.5s" }}
        />
      )}
    </button>
  );
}

export { SIDEBAR_EXPANDED, SIDEBAR_COLLAPSED };
